import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { CustomerAgeingData } from '@/types/ageing-analysis';

// Log Call Dialog
interface LogCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CustomerAgeingData | null;
}

export function LogCallDialog({ open, onOpenChange, customer }: LogCallDialogProps) {
  const [notes, setNotes] = useState('');

  const handleSave = () => {
    if (!notes.trim()) return;
    toast.success(`Call logged for ${customer?.customerName}`);
    setNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Log Call/Note</DialogTitle>
          <DialogDescription>Record interaction with {customer?.customerName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Enter call notes or interaction details..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!notes.trim()}>
            Save Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Send Email Dialog
interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CustomerAgeingData | null;
}

export function SendEmailDialog({ open, onOpenChange, customer }: SendEmailDialogProps) {
  const [template, setTemplate] = useState('');

  const handleSend = () => {
    if (!template) return;
    toast.success(`Reminder email sent to ${customer?.customerName}`);
    setTemplate('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Reminder Email</DialogTitle>
          <DialogDescription>Select email template for {customer?.customerName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="template">Email Template</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger id="template">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="friendly-reminder">Friendly Reminder</SelectItem>
                <SelectItem value="payment-overdue">Payment Overdue Notice</SelectItem>
                <SelectItem value="final-notice">Final Notice</SelectItem>
                <SelectItem value="payment-plan-offer">Payment Plan Offer</SelectItem>
                <SelectItem value="escalation-warning">Escalation Warning</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!template}>
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Create Promise Dialog
interface CreatePromiseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CustomerAgeingData | null;
}

export function CreatePromiseDialog({ open, onOpenChange, customer }: CreatePromiseDialogProps) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');

  const handleSave = () => {
    if (!amount || !date) return;
    toast.success(`Promise to pay created for ${customer?.customerName}`);
    setAmount('');
    setDate('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Promise to Pay</DialogTitle>
          <DialogDescription>Record payment commitment from {customer?.customerName}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Promise Amount</Label>
            <Input
              id="amount"
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date">Promise Date</Label>
            <Input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!amount || !date}>
            Save Promise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Assign Clerk Dialog
interface AssignClerkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CustomerAgeingData | null;
}

export function AssignClerkDialog({ open, onOpenChange, customer }: AssignClerkDialogProps) {
  const [clerk, setClerk] = useState('');

  const handleAssign = () => {
    if (!clerk) return;
    toast.success(`${customer?.customerName} assigned to ${clerk}`);
    setClerk('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign to Clerk</DialogTitle>
          <DialogDescription>Assign {customer?.customerName} to collection clerk</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="clerk">Clerk</Label>
            <Select value={clerk} onValueChange={setClerk}>
              <SelectTrigger id="clerk">
                <SelectValue placeholder="Select a clerk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="john-smith">John Smith</SelectItem>
                <SelectItem value="sarah-johnson">Sarah Johnson</SelectItem>
                <SelectItem value="michael-brown">Michael Brown</SelectItem>
                <SelectItem value="emily-davis">Emily Davis</SelectItem>
                <SelectItem value="david-wilson">David Wilson</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!clerk}>
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
